// Zenyth Cart Announcements - Accessibility improvements for cart drawer and cart page
// Monitors cart changes and announces updates to screen readers

(function() {
  'use strict';

  console.log('🛒 Zenyth Cart Announcements: Script loaded!');

  // Configuration
  const CONFIG = {
    // Selectors for cart drawer
    drawer: {
      container: '#CartDrawer-CartItems',
      items: '.cart-item',
      itemTitle: '.h3_cart_item',
      itemQuantity: '.quantity__input',
      itemPrice: '.price_regular, .price',
      subtotal: '.totals__total-value',
      cartCount: '#cart-count',
      liveRegionId: 'znt-cart-drawer-liveregion'
    },
    // Selectors for cart page
    page: {
      container: '.cart-items__wrapper, .cart-items',
      items: '[ref="cartItemRows[]"]',
      itemTitle: '.cart-items__title',
      itemQuantity: 'input[data-cart-line]',
      itemPrice: '.cart-items__price text-component',
      subtotal: '[ref="cartTotal"]',
      cartCount: '[ref="cartBubbleCount"]',
      liveRegionId: 'znt-cart-page-liveregion'
    }
  };

  let cartState = {
    totalItems: 0,
    subtotal: '',
    items: []
  };

  let isInitialized = false;
  let observer = null;
  let lastAnnouncementTime = 0;
  let announcementDebounceTimer = null;
  const ANNOUNCEMENT_DEBOUNCE_MS = 1000; // Wait 1 second between announcements

  // Initialize based on context
  function init() {
    if (isInitialized) return;
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupCartMonitoring);
    } else {
      setupCartMonitoring();
    }
  }

  // Setup cart monitoring
  function setupCartMonitoring() {
    // Check if we're on cart page or cart drawer is present
    const isCartPage = window.location.pathname.includes('/cart');
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (isCartPage) {
      console.log('🛒 Cart page detected, setting up monitoring...');
      setupCartPageMonitoring();
    }
    
    if (cartDrawer) {
      console.log('🛒 Cart drawer detected, setting up monitoring...');
      setupCartDrawerMonitoring();
    }

    // Also monitor for cart drawer opening/closing
    if (cartDrawer) {
      observeCartDrawer(cartDrawer);
    }

    isInitialized = true;
  }

  // Setup monitoring for cart page
  function setupCartPageMonitoring() {
    const config = CONFIG.page;
    const container = document.querySelector(config.container);
    
    if (!container) {
      console.warn('⚠️ Cart page container not found');
      return;
    }

    // Create live region if it doesn't exist
    createLiveRegion(config.liveRegionId);

    // Initial state capture
    setTimeout(() => {
      cartState = extractCartState(config);
      console.log('🛒 Initial cart page state:', cartState);
    }, 1000);

    // Setup mutation observer
    observer = new MutationObserver(() => {
      setTimeout(() => {
        const newState = extractCartState(config);
        detectAndAnnounceChanges(newState, config);
      }, 300);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log('✅ Cart page monitoring active');
  }

  // Setup monitoring for cart drawer
  function setupCartDrawerMonitoring() {
    const config = CONFIG.drawer;
    
    // Create live region if it doesn't exist
    createLiveRegion(config.liveRegionId);

    // Store previous state before reload
    let previousStateBeforeReload = null;

    // Function to capture state before potential reload
    function captureStateBeforeReload() {
      const container = document.querySelector(config.container);
      if (container && container.querySelector(config.items)) {
        previousStateBeforeReload = extractCartState(config);
        console.log('📸 [setupCartDrawerMonitoring] Captured state before potential reload:', previousStateBeforeReload);
      }
    }


    // Function to check and setup drawer when it opens
    function checkDrawer() {
      const container = document.querySelector(config.container);
      console.log('🔍 [checkDrawer] Checking drawer, container found:', !!container);
      
      if (container) {
        const hasItems = container.querySelector(config.items);
        console.log('🔍 [checkDrawer] Has items:', !!hasItems);
        
        if (hasItems) {
          // Initial state capture
          setTimeout(() => {
            const newState = extractCartState(config);
            console.log('🛒 [checkDrawer] Initial cart drawer state:', newState);
            
            // If we have a previous state, compare
            if (previousStateBeforeReload) {
              console.log('🔄 [checkDrawer] Comparing with previous state before reload');
              detectAndAnnounceChanges(newState, config);
              previousStateBeforeReload = null; // Clear after comparison
            } else {
              // First time, just store
              cartState = newState;
            }
          }, 800); // Increased delay to ensure DOM is fully rendered

          // Setup mutation observer for drawer - detect complete replacement
          if (observer) {
            observer.disconnect();
            console.log('🔄 [checkDrawer] Disconnected previous observer');
          }

          observer = new MutationObserver((mutations) => {
            let shouldCheck = false;
            let hasRemoval = false;
            
            mutations.forEach((mutation) => {
              // Detect when content is completely replaced (innerHTML change)
              if (mutation.type === 'childList') {
                // If nodes were removed, it might be a complete reload
                if (mutation.removedNodes.length > 0) {
                  console.log('🔄 [checkDrawer] MutationObserver: Nodes removed:', mutation.removedNodes.length);
                  // Check if it's a significant removal (like the entire wrapper)
                  mutation.removedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                      if (node.classList && (node.classList.contains('drawer__cart-items-wrapper') || 
                          node.classList.contains('cart-item'))) {
                        console.log('🔄 [checkDrawer] Significant node removed:', node.className);
                        hasRemoval = true;
                        // Capture state before reload
                        captureStateBeforeReload();
                      }
                    }
                  });
                  shouldCheck = true;
                }
                
                // If nodes were added, check after a delay
                if (mutation.addedNodes.length > 0) {
                  console.log('🔄 [checkDrawer] MutationObserver: Nodes added:', mutation.addedNodes.length);
                  mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                      if (node.classList && (node.classList.contains('drawer__cart-items-wrapper') || 
                          node.classList.contains('cart-item') ||
                          (node.querySelector && node.querySelector('.cart-item')))) {
                        console.log('🔄 [checkDrawer] Significant node added:', node.className || 'with cart-item');
                        shouldCheck = true;
                      }
                    }
                  });
                }
              }
              
              // Detect text/character changes (quantity updates, etc.)
              if (mutation.type === 'characterData') {
                console.log('🔄 [checkDrawer] MutationObserver: Character data changed');
                shouldCheck = true;
              }
            });

            if (shouldCheck) {
              console.log('🔄 [checkDrawer] MutationObserver: Scheduling state check (hasRemoval:', hasRemoval, ')');
              const delay = hasRemoval ? 1000 : 500; // Longer delay if it was a removal (complete reload)
              setTimeout(() => {
                const newState = extractCartState(config);
                if (hasRemoval && previousStateBeforeReload) {
                  console.log('🔄 [checkDrawer] Comparing after removal');
                  detectAndAnnounceChanges(newState, config);
                  previousStateBeforeReload = null;
                } else {
                  detectAndAnnounceChanges(newState, config);
                }
              }, delay);
            }
          });

          observer.observe(container, {
            childList: true,
            subtree: true,
            characterData: true
          });

          // Also observe the parent to catch when the wrapper is replaced
          const parent = container.parentElement;
          if (parent) {
            console.log('🔍 [checkDrawer] Also observing parent element:', parent.className);
            const parentObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                  if (mutation.removedNodes.length > 0) {
                    mutation.removedNodes.forEach(node => {
                      if (node.nodeType === 1 && node.classList && 
                          (node.classList.contains('drawer__cart-items-wrapper') || 
                           node.id === 'CartDrawer-CartItems')) {
                        console.log('🔄 [checkDrawer] Parent observer: Container/wrapper removed!');
                        captureStateBeforeReload();
                      }
                    });
                  }
                  if (mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach(node => {
                      if (node.nodeType === 1 && node.classList && 
                          (node.classList.contains('drawer__cart-items-wrapper') || 
                           node.id === 'CartDrawer-CartItems')) {
                        console.log('🔄 [checkDrawer] Parent observer: Container/wrapper added!');
                        setTimeout(() => {
                          const newState = extractCartState(config);
                          if (previousStateBeforeReload) {
                            console.log('🔄 [checkDrawer] Parent observer: Comparing states');
                            detectAndAnnounceChanges(newState, config);
                            previousStateBeforeReload = null;
                          } else {
                            cartState = newState;
                          }
                        }, 1000);
                      }
                    });
                  }
                }
              });
            });

            parentObserver.observe(parent, {
              childList: true,
              subtree: false
            });
            console.log('✅ [checkDrawer] Parent observer active');
          }

          console.log('✅ [checkDrawer] Cart drawer monitoring active');
        } else {
          console.log('⚠️ [checkDrawer] Container found but no items yet');
        }
      } else {
        console.warn('⚠️ [checkDrawer] Container not found:', config.container);
      }
    }

    // Intercept cart drawer renderContents method FIRST
    const drawerElement = document.querySelector('cart-drawer');
    if (drawerElement) {
      console.log('🔍 [setupCartDrawerMonitoring] Cart drawer element found, setting up intercept');
      
      // Intercept renderContents if it exists
      if (drawerElement.renderContents && typeof drawerElement.renderContents === 'function') {
        console.log('🔧 [setupCartDrawerMonitoring] Intercepting renderContents method');
        const originalRenderContents = drawerElement.renderContents.bind(drawerElement);
        
        drawerElement.renderContents = function(parsedState, cancelOpen) {
          console.log('🔄 [renderContents INTERCEPT] renderContents called - capturing state before render');
          captureStateBeforeReload();
          
          // Call original method
          const result = originalRenderContents(parsedState, cancelOpen);
          
          // After render, wait for DOM to update and compare
          setTimeout(() => {
            console.log('🔄 [renderContents INTERCEPT] renderContents completed - checking new state');
            const newState = extractCartState(config);
            if (previousStateBeforeReload) {
              console.log('🔄 [renderContents INTERCEPT] Comparing states after renderContents');
              console.log('🔄 [renderContents INTERCEPT] Old state:', previousStateBeforeReload);
              console.log('🔄 [renderContents INTERCEPT] New state:', newState);
              detectAndAnnounceChanges(newState, config);
              previousStateBeforeReload = null;
            } else {
              console.log('🔄 [renderContents INTERCEPT] No previous state, storing new state');
              cartState = newState;
            }
          }, 1000); // Increased delay to ensure DOM is fully updated
          
          return result;
        };
      } else {
        console.warn('⚠️ [setupCartDrawerMonitoring] renderContents method not found or not a function');
      }
      
      // Also listen for cart drawer updates via DOM changes
      console.log('🔍 [setupCartDrawerMonitoring] Setting up drawer element observer');
      
      // Monitor for drawer content updates (complete replacement)
      const drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            console.log('🔄 [setupCartDrawerMonitoring] Drawer element changed, re-checking');
            // Capture state before reload if container exists
            captureStateBeforeReload();
            // Re-check drawer after a delay
            setTimeout(() => {
              checkDrawer();
            }, 500);
          }
        });
      });

      drawerObserver.observe(drawerElement, {
        childList: true,
        subtree: true
      });
    } else {
      console.warn('⚠️ [setupCartDrawerMonitoring] Cart drawer element not found');
    }

    // Check immediately
    checkDrawer();
  }

  // Observe cart drawer element for open/close
  function observeCartDrawer(cartDrawer) {
    const drawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isOpen = cartDrawer.classList.contains('active') || 
                        !cartDrawer.classList.contains('is-empty');
          if (isOpen) {
            setTimeout(() => {
              setupCartDrawerMonitoring();
            }, 500);
          }
        }
      });
    });

    drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  // Extract current cart state
  function extractCartState(config) {
    console.log('🔍 [extractCartState] Starting extraction with config:', config);
    const state = {
      totalItems: 0,
      subtotal: '',
      items: []
    };

    // Get total items from cart count
    const cartCountEl = document.querySelector(config.cartCount);
    console.log('🔍 [extractCartState] Cart count element:', cartCountEl ? 'found' : 'NOT FOUND', 'selector:', config.cartCount);
    if (cartCountEl) {
      const countText = cartCountEl.textContent.trim();
      state.totalItems = parseInt(countText) || 0;
      console.log('🔍 [extractCartState] Cart count:', state.totalItems, 'from text:', countText);
    } else {
      console.warn('⚠️ [extractCartState] Cart count element not found with selector:', config.cartCount);
    }

    // Get subtotal
    const subtotalEl = document.querySelector(config.subtotal);
    console.log('🔍 [extractCartState] Subtotal element:', subtotalEl ? 'found' : 'NOT FOUND', 'selector:', config.subtotal);
    if (subtotalEl) {
      state.subtotal = subtotalEl.textContent.trim();
      console.log('🔍 [extractCartState] Subtotal:', state.subtotal);
    } else {
      console.warn('⚠️ [extractCartState] Subtotal element not found with selector:', config.subtotal);
    }

    // Get items
    const container = document.querySelector(config.container);
    console.log('🔍 [extractCartState] Container element:', container ? 'found' : 'NOT FOUND', 'selector:', config.container);
    if (container) {
      const itemElements = container.querySelectorAll(config.items);
      console.log('🔍 [extractCartState] Found', itemElements.length, 'items with selector:', config.items);
      
      itemElements.forEach((itemEl, index) => {
        try {
          const titleEl = itemEl.querySelector(config.itemTitle);
          const quantityEl = itemEl.querySelector(config.itemQuantity);
          const priceEl = itemEl.querySelector(config.itemPrice);

          console.log(`🔍 [extractCartState] Item ${index}:`, {
            titleEl: titleEl ? 'found' : 'NOT FOUND',
            quantityEl: quantityEl ? 'found' : 'NOT FOUND',
            priceEl: priceEl ? 'found' : 'NOT FOUND'
          });

          if (titleEl && quantityEl) {
            const title = titleEl.textContent.trim();
            const quantity = parseInt(quantityEl.value || quantityEl.textContent) || 0;
            const price = priceEl ? priceEl.textContent.trim() : '';
            
            // Create unique key for item
            const itemKey = itemEl.id || 
                          itemEl.getAttribute('data-key') || 
                          `item-${index}-${title.substring(0, 20)}`;

            const itemData = {
              key: itemKey,
              title: title,
              quantity: quantity,
              price: price,
              index: index
            };

            state.items.push(itemData);
            console.log(`🔍 [extractCartState] Item ${index} extracted:`, itemData);
          } else {
            console.warn(`⚠️ [extractCartState] Item ${index} missing required elements`);
          }
        } catch (error) {
          console.warn('⚠️ [extractCartState] Error extracting item:', error);
        }
      });
    } else {
      console.warn('⚠️ [extractCartState] Container not found');
    }

    console.log('🔍 [extractCartState] Final state:', state);
    return state;
  }

  // Detect changes and announce
  function detectAndAnnounceChanges(newState, config) {
    console.log('🔍 [detectAndAnnounceChanges] Starting detection');
    console.log('🔍 [detectAndAnnounceChanges] Old state:', cartState);
    console.log('🔍 [detectAndAnnounceChanges] New state:', newState);

    // If no previous state, just store the new state
    if (!cartState || cartState.items.length === 0) {
      console.log('🔍 [detectAndAnnounceChanges] No previous state, storing initial state');
      cartState = newState;
      return;
    }

    const changes = {
      hasChanges: false,
      totalItemsChanged: false,
      subtotalChanged: false,
      itemsChanged: [],
      itemsAdded: [],
      itemsRemoved: []
    };

    // Check total items
    if (cartState.totalItems !== newState.totalItems) {
      changes.totalItemsChanged = true;
      changes.hasChanges = true;
      console.log('🔍 [detectAndAnnounceChanges] Total items changed:', cartState.totalItems, '→', newState.totalItems);
    }

    // Check subtotal
    if (cartState.subtotal !== newState.subtotal) {
      changes.subtotalChanged = true;
      changes.hasChanges = true;
      console.log('🔍 [detectAndAnnounceChanges] Subtotal changed:', cartState.subtotal, '→', newState.subtotal);
    }

    // Compare items
    const oldItemsMap = new Map(cartState.items.map(item => [item.key, item]));
    const newItemsMap = new Map(newState.items.map(item => [item.key, item]));

    console.log('🔍 [detectAndAnnounceChanges] Old items keys:', Array.from(oldItemsMap.keys()));
    console.log('🔍 [detectAndAnnounceChanges] New items keys:', Array.from(newItemsMap.keys()));

    // Find added and changed items
    for (const [key, newItem] of newItemsMap) {
      const oldItem = oldItemsMap.get(key);
      
      if (!oldItem) {
        console.log('🔍 [detectAndAnnounceChanges] Item added:', newItem.title);
        changes.itemsAdded.push(newItem);
        changes.hasChanges = true;
      } else {
        // Check if item changed
        const quantityChanged = oldItem.quantity !== newItem.quantity;
        const priceChanged = oldItem.price !== newItem.price;
        const titleChanged = oldItem.title !== newItem.title;
        
        if (quantityChanged || priceChanged || titleChanged) {
          console.log('🔍 [detectAndAnnounceChanges] Item changed:', {
            title: newItem.title,
            quantityChanged: quantityChanged,
            priceChanged: priceChanged,
            titleChanged: titleChanged,
            oldQty: oldItem.quantity,
            newQty: newItem.quantity
          });
          changes.itemsChanged.push({
            old: oldItem,
            new: newItem
          });
          changes.hasChanges = true;
        }
      }
    }

    // Find removed items
    for (const [key, oldItem] of oldItemsMap) {
      if (!newItemsMap.has(key)) {
        console.log('🔍 [detectAndAnnounceChanges] Item removed:', oldItem.title);
        changes.itemsRemoved.push(oldItem);
        changes.hasChanges = true;
      }
    }

    console.log('🔍 [detectAndAnnounceChanges] Changes detected:', changes);

    // Announce changes if any (with debouncing)
    if (changes.hasChanges) {
      const now = Date.now();
      const timeSinceLastAnnouncement = now - lastAnnouncementTime;
      
      console.log('🔍 [detectAndAnnounceChanges] Time since last announcement:', timeSinceLastAnnouncement, 'ms');
      
      // Clear any pending announcement
      if (announcementDebounceTimer) {
        clearTimeout(announcementDebounceTimer);
        console.log('🔍 [detectAndAnnounceChanges] Cleared pending announcement');
      }

      // Debounce announcements to avoid spam
      announcementDebounceTimer = setTimeout(() => {
        console.log('🔍 [detectAndAnnounceChanges] Debounce timer fired, announcing changes');
        announceChanges(changes, config, newState);
        cartState = newState; // Update stored state
        lastAnnouncementTime = Date.now();
        announcementDebounceTimer = null;
      }, ANNOUNCEMENT_DEBOUNCE_MS);
    } else {
      console.log('🔍 [detectAndAnnounceChanges] No changes detected');
    }
  }

  // Announce changes via live region
  function announceChanges(changes, config, newState) {
    console.log('🔊 [announceChanges] Starting announcement with changes:', changes);
    const announcementParts = [];

    // Item changes - announce quantity changes with subtotal
    changes.itemsChanged.forEach(change => {
      const { old: oldItem, new: newItem } = change;
      if (oldItem.quantity !== newItem.quantity) {
        const message = `${newItem.title}: quantity updated to ${newItem.quantity}`;
        announcementParts.push(message);
        console.log('🔊 [announceChanges] Adding quantity change:', message);
      }
      // Skip price/title only changes to reduce noise
    });

    // Items added - announce with subtotal
    if (changes.itemsAdded.length > 0) {
      changes.itemsAdded.forEach(item => {
        const message = `${item.title}: ${item.quantity} ${item.quantity === 1 ? 'item' : 'items'} added to cart`;
        announcementParts.push(message);
        console.log('🔊 [announceChanges] Adding item added:', message);
      });
    }

    // Items removed - announce item name and that it was removed
    if (changes.itemsRemoved.length > 0) {
      changes.itemsRemoved.forEach(item => {
        const message = `${item.title}: removed from cart`;
        announcementParts.push(message);
        console.log('🔊 [announceChanges] Adding item removed:', message);
      });
    }

    // Total items change - announce if items were added/removed (not just quantity changes)
    if (changes.totalItemsChanged && newState && 
        (changes.itemsAdded.length > 0 || changes.itemsRemoved.length > 0)) {
      const message = `Total items: ${newState.totalItems}`;
      announcementParts.push(message);
      console.log('🔊 [announceChanges] Adding total items change:', message);
    }

    // ALWAYS announce subtotal if it changed OR if there were any item changes
    // This ensures subtotal is always announced with quantity changes, additions, or removals
    if (newState && (changes.subtotalChanged || 
        changes.itemsChanged.length > 0 || 
        changes.itemsAdded.length > 0 || 
        changes.itemsRemoved.length > 0)) {
      // Extract current subtotal (it might have changed even if flag wasn't set)
      const currentSubtotal = newState.subtotal || extractCartState(config).subtotal;
      if (currentSubtotal) {
        const message = `Subtotal: ${currentSubtotal}`;
        announcementParts.push(message);
        console.log('🔊 [announceChanges] Adding subtotal:', message);
      }
    }

    // Announce via live region
    if (announcementParts.length > 0) {
      const message = announcementParts.join('. ');
      const liveRegion = document.getElementById(config.liveRegionId);
      
      console.log('🔊 [announceChanges] Final announcement message:', message);
      console.log('🔊 [announceChanges] Live region element:', liveRegion ? 'found' : 'NOT FOUND', 'ID:', config.liveRegionId);
      
      if (liveRegion) {
        // Force screen reader to read by clearing first, then setting content
        // This ensures the announcement takes priority
        liveRegion.textContent = '';
        liveRegion.setAttribute('aria-live', 'off'); // Temporarily disable to force re-read
        
        // Use requestAnimationFrame to ensure DOM update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Re-enable assertive mode
            liveRegion.setAttribute('aria-live', 'assertive');
            liveRegion.textContent = message;
            
            // Force focus to live region briefly to ensure announcement (but don't visually focus)
            const originalTabIndex = liveRegion.getAttribute('tabindex');
            liveRegion.setAttribute('tabindex', '-1');
            
            // Focus it programmatically (screen readers will read it)
            liveRegion.focus();
            
            // Immediately blur to not interfere with user's current focus
            setTimeout(() => {
              liveRegion.blur();
              if (originalTabIndex !== null) {
                liveRegion.setAttribute('tabindex', originalTabIndex);
              }
            }, 10);
            
            console.log('🔊 [announceChanges] Message set in live region with assertive priority:', message);
            
            // Clear after announcement (longer delay to ensure it's read)
            setTimeout(() => {
              liveRegion.textContent = '';
              console.log('🔊 [announceChanges] Live region cleared');
            }, 8000); // Increased from 5000 to 8000 to ensure it's fully read
          });
        });
      } else {
        console.error('❌ [announceChanges] Live region not found:', config.liveRegionId);
        console.error('❌ [announceChanges] Available live regions:', 
          Array.from(document.querySelectorAll('[role="status"]')).map(el => el.id));
      }
    } else {
      console.log('🔊 [announceChanges] No announcement parts, skipping');
    }
  }

  // Create live region element
  function createLiveRegion(id) {
    if (document.getElementById(id)) {
      return; // Already exists
    }

    const liveRegion = document.createElement('div');
    liveRegion.id = id;
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'assertive'); // Changed to assertive for priority
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('aria-relevant', 'additions text');
    liveRegion.className = 'visually-hidden';
    // Ensure it's accessible but hidden
    liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0);';
    liveRegion.setAttribute('tabindex', '-1'); // Make it focusable for screen readers
    
    document.body.appendChild(liveRegion);
    console.log('✅ Live region created:', id, 'with assertive priority');
  }

  // Initialize
  init();

  // Re-initialize when cart drawer is updated via Shopify's section rendering
  if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail && event.detail.sectionId === 'cart-drawer') {
        console.log('🛒 Cart drawer section reloaded, re-initializing...');
        isInitialized = false;
        cartState = { totalItems: 0, subtotal: '', items: [] };
        setTimeout(init, 500);
      }
    });
  }

})();


// ===========================================================================
// Zenyth a11y DOM fixes - structural defects best solved with a JS transform.
// Each fix is isolated in try/catch so one failure never blocks the others.
// Added 2026-06-05 for Global Defects CSV rows that authored content / dynamic
// markup made impractical to fix in Liquid alone. VALIDATE in live preview.
// ===========================================================================
(function () {
  'use strict';

  function run() {
    // --- #37519: PDP description sub-labels ("FIT + FABRIC", "PRODUCT DETAILS")
    // are authored as <p><strong>...</strong></p> and function as headings. Expose
    // them as headings (a <p> whose entire content is a single <strong>).
    try {
      document.querySelectorAll('.accordion-content p, .product__description p').forEach(function (p) {
        if (p.getAttribute('role') === 'heading') return;
        var kids = Array.prototype.filter.call(p.childNodes, function (n) {
          return !(n.nodeType === 3 && !n.textContent.trim());
        });
        if (kids.length === 1 && kids[0].nodeType === 1 && kids[0].tagName === 'STRONG' &&
            p.textContent.trim() === kids[0].textContent.trim() && p.textContent.trim().length <= 60) {
          p.setAttribute('role', 'heading');
          p.setAttribute('aria-level', '4');
        }
      });
    } catch (e) { /* #37519 */ }

    // --- #38006: Compare "weight" cell shows Ultra Light / Lightweight / Midweight /
    // Heavyweight with the product's weight bolded (<strong>) but no programmatic
    // "current" state. Mark the bolded one as current. (List semantics omitted on
    // purpose so the <td> keeps its table-cell association from #37995.)
    try {
      document.querySelectorAll('.weight.compare-card-section').forEach(function (cell) {
        var current = null;
        Array.prototype.forEach.call(cell.children, function (el) {
          if (el.tagName === 'P' && el.querySelector('strong')) current = el;
        });
        if (current && !current.getAttribute('aria-current')) {
          current.setAttribute('aria-current', 'true');
        }
      });
    } catch (e) { /* #38006 */ }

    // --- #37489: quantity <input> carries `disabled` (at min qty), which hides its
    // value from screen readers and blocks typing. Keep the input enabled.
    try {
      var enableQty = function (root) {
        (root || document).querySelectorAll('.quantity__input[disabled]').forEach(function (i) {
          i.removeAttribute('disabled');
        });
      };
      enableQty();
      new MutationObserver(function () { enableQty(); }).observe(document.body, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['disabled']
      });
      document.addEventListener('shopify:section:load', function () { enableQty(); });
    } catch (e) { /* #37489 */ }

    // --- #37901: the "Skip to content" link is rendered after the header; it must be
    // the first focusable element in the DOM.
    try {
      var skip = document.querySelector('.skip-to-content-link');
      if (skip && document.body && document.body.firstElementChild !== skip) {
        document.body.insertBefore(skip, document.body.firstChild);
      }
    } catch (e) { /* #37901 */ }

    // --- #38009: a body-copy paragraph in the contentful section is marked as <h5>.
    // Demote long "headings" (clearly sentences) to presentation so they leave the
    // heading outline.
    try {
      document.querySelectorAll('.contentful-left h5, .contentful-right h5').forEach(function (h) {
        if (h.textContent.trim().length > 60 && h.getAttribute('role') !== 'presentation') {
          h.setAttribute('role', 'presentation');
        }
      });
    } catch (e) { /* #38009 */ }

    // --- #37896: the hero "heading" block renders an sr-only <h1>{{ page_title }}</h1>
    // (sections/hero.liquid). When a page stacks more than one hero, the same page-title
    // h1 renders multiple times - the duplicates are content wrongly marked as a heading.
    // Keep the first; demote subsequent identical sr-only h1s to presentation so the page
    // has exactly one h1. Add-only, no visual change (the elements are sr-only).
    try {
      var srH1s = document.querySelectorAll('h1.sr-only');
      var seenH1 = {};
      srH1s.forEach(function (h) {
        var key = h.textContent.trim().toLowerCase();
        if (!key) return;
        if (seenH1[key]) {
          h.setAttribute('role', 'presentation');
        } else {
          seenH1[key] = true;
        }
      });
    } catch (e) { /* #37896 */ }

    // --- #37487 + #37552: cart focus management. The theme re-renders the cart
    // ~2-3s after a change (and can re-drop focus more than once in that window),
    // landing keyboard focus on <body>. Rather than guess which event/observer fires,
    // we poll for a short window after a user cart action and re-assert focus whenever
    // it has fallen to <body> - and NEVER when focus is on a real element (so we don't
    // fight legitimate focus moves). VALIDATE in live preview.
    try {
      var zntCartAction = null, zntExpiry = 0, zntPoll = null;

      var zntApplyFocus = function () {
        if (!zntCartAction || Date.now() > zntExpiry) return;
        var ae = document.activeElement;
        if (ae && ae !== document.body && ae !== document.documentElement) return; // focus not lost - leave it
        var a = zntCartAction;
        if (a.type === 'remove') {                                                   // #37552
          var empty = document.querySelector('.is-empty .cart__empty-text, cart-items.is-empty .cart__empty-text');
          if (empty) { empty.setAttribute('tabindex', '-1'); empty.focus(); return; }
        }
        if (a.type === 'qty' && a.index != null) {                                   // #37487
          var input = document.querySelector('.quantity__input[data-index="' + a.index + '"]');
          var qi = input && input.closest('quantity-input');
          if (!qi) return;
          var same  = qi.querySelector('.quantity__button[name="' + a.name + '"]');
          var other = qi.querySelector('.quantity__button[name="' + (a.name === 'minus' ? 'plus' : 'minus') + '"]');
          if (same && !same.disabled) same.focus();
          else if (other && !other.disabled) other.focus();
        }
      };

      var zntArm = function (action) {
        zntCartAction = action;
        zntExpiry = Date.now() + 5000;
        clearInterval(zntPoll);
        // Poll every 250ms for ~5s: re-assert focus through every re-render/re-drop in
        // the window. Stops at expiry; harmless once focus sits on a real control.
        zntPoll = setInterval(function () {
          if (Date.now() > zntExpiry) { clearInterval(zntPoll); zntCartAction = null; return; }
          zntApplyFocus();
        }, 250);
      };

      document.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        var qtyBtn = e.target.closest('.quantity__button');
        if (qtyBtn) {
          var qi = qtyBtn.closest('quantity-input');
          var input = qi && qi.querySelector('.quantity__input');
          zntArm({ type: 'qty', name: qtyBtn.getAttribute('name'),
                   index: input ? input.getAttribute('data-index') : null });
        } else if (e.target.closest('cart-remove-button, .cart-item__remove')) {
          zntArm({ type: 'remove' });
        }
      }, true);
    } catch (e) { /* #37487 + #37552 */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
